import { generateClient } from "aws-amplify/data";
import type { Schema } from "amplify/data/resource";
import type {
  ActivationBonusCampaignRow,
  ActivationBonusPayoutStatus,
  ActivationBonusStage,
  RetryActivationBonusPayoutResult,
  RetryPayoutStatus,
} from "./types";

const PAYOUT_STATUSES: ActivationBonusPayoutStatus[] = [
  "pending",
  "succeeded",
  "failed",
];

const ACTIVATION_BONUS_STAGES: ActivationBonusStage[] = [
  "empty",
  "not_started",
  "eligible",
  "paid",
  "expired",
  "failed",
];

function normalizePayoutStatus(
  value: unknown,
): ActivationBonusPayoutStatus | null {
  if (typeof value !== "string") return null;
  return PAYOUT_STATUSES.includes(value as ActivationBonusPayoutStatus)
    ? (value as ActivationBonusPayoutStatus)
    : null;
}

/**
 * Returns null for a stage the client doesn't recognize. Guessing a stage here
 * would mislabel the row — the previous fallback reported every unrecognized
 * value, `not_started` included, as `eligible`.
 */
function normalizeStage(value: unknown): ActivationBonusStage | null {
  if (typeof value !== "string") return null;
  return ACTIVATION_BONUS_STAGES.includes(value as ActivationBonusStage)
    ? (value as ActivationBonusStage)
    : null;
}

function normalizeRetryStatus(value: unknown): RetryPayoutStatus {
  const valid: RetryPayoutStatus[] = [
    "succeeded",
    "still_failed",
    "cap_reached",
    "ineligible",
    "error",
  ];
  if (typeof value !== "string") return "error";
  return valid.includes(value as RetryPayoutStatus)
    ? (value as RetryPayoutStatus)
    : "error";
}

const client = generateClient<Schema>();

export interface ListActivationBonusCampaignsPage {
  items: ActivationBonusCampaignRow[];
  nextToken: string | null;
}

export class ActivationBonusRepository {
  async list(opts: {
    nextToken?: string | null;
    limit?: number;
    stages?: ActivationBonusStage[];
    createdAfter?: string | null;
    createdBefore?: string | null;
  }): Promise<ListActivationBonusCampaignsPage> {
    const { data, errors } = await client.queries.ListActivationBonusCampaigns({
      input: {
        nextToken: opts.nextToken ?? null,
        limit: opts.limit ?? null,
        stages: opts.stages?.length ? opts.stages : null,
        createdAfter: opts.createdAfter ?? null,
        createdBefore: opts.createdBefore ?? null,
      },
    });

    if (errors?.length) throw new Error(errors[0].message);
    if (!data) return { items: [], nextToken: null };

    const items: ActivationBonusCampaignRow[] = (
      (data as { items?: unknown[] }).items ?? []
    )
      .filter((row): row is NonNullable<typeof row> => row !== null)
      .map((row) => {
        const r = row as Record<string, unknown>;
        return {
          businessId: String(r["businessId"] ?? ""),
          businessName:
            typeof r["businessName"] === "string" ? r["businessName"] : null,
          email: typeof r["email"] === "string" ? r["email"] : null,
          activationBonusStage: normalizeStage(r["activationBonusStage"]),
          accountCreatedAt:
            typeof r["accountCreatedAt"] === "string"
              ? r["accountCreatedAt"]
              : null,
          startedAt: typeof r["startedAt"] === "string" ? r["startedAt"] : null,
          qualifyingTransactionId:
            typeof r["qualifyingTransactionId"] === "string"
              ? r["qualifyingTransactionId"]
              : null,
          payoutStatus: normalizePayoutStatus(r["payoutStatus"]),
          attemptCount:
            typeof r["attemptCount"] === "number" ? r["attemptCount"] : 0,
          lastErrorMessage:
            typeof r["lastErrorMessage"] === "string"
              ? r["lastErrorMessage"]
              : null,
        };
      });

    return {
      items,
      nextToken:
        typeof (data as Record<string, unknown>)["nextToken"] === "string"
          ? ((data as Record<string, unknown>)["nextToken"] as string)
          : null,
    };
  }

  async retryPayout(
    businessId: string,
  ): Promise<RetryActivationBonusPayoutResult> {
    const { data, errors } = await client.mutations.RetryActivationBonusPayout({
      input: { businessId },
    });

    if (errors?.length) throw new Error(errors[0].message);
    if (!data)
      throw new Error("Empty response from RetryActivationBonusPayout");

    const d = data as Record<string, unknown>;
    return {
      status: normalizeRetryStatus(d["status"]),
      message: typeof d["message"] === "string" ? d["message"] : null,
    };
  }
}
